/**
 * MessageThread — the conversation, as a chronological exchange (055 US1).
 *
 * Extracted from `CaseDetail` during the 055 prototype convergence and restyled
 * to the prototype's message blocks: the member's own messages tinted with the
 * brand primary, the fund's on the card surface, each with a right-aligned
 * timestamp carrying the TIME OF DAY (FR-001).
 *
 * ── Attribution keys off DIRECTION, never the author string (FR-002) ────────
 *
 * This logic is carried over unchanged from `CaseDetail` (038 FR-005) and is
 * subtler than it looks. A case is member-initiated, and the integration stamps
 * its OWN service identity (`integration-service`, IS #90) on the member's own
 * posts — so the author field CANNOT tell the member's message from a fund
 * reply. `isReply` can:
 *
 *   isReply === false → the member's own message      → "Sinä"
 *   isReply === true  → a fund/handler reply          → the real handler name,
 *                       or, when the author is the (never-shown-verbatim)
 *                       service identity or absent, the fund attribution
 *                       "YTK Työelämä"
 *
 * Do NOT "simplify" this back to reading `author`. It will look correct in
 * fixtures and mislabel every real member message as coming from the fund.
 *
 * The prototype labels every fund message "YTK Työelämä"; Oma+ shows the real
 * handler's name when the back office supplies one, which is strictly more
 * informative and still satisfies FR-002's "attributed to a named sender".
 * The prototype's separate "Vastaus" marker is dropped — the sender label and
 * the block tint already carry the distinction, so it was redundant chrome.
 */

import type { JSX } from 'react';

import type { LanguageCode } from '../i18n/LanguagePort.js';
import { useTranslate } from '../i18n/useTranslate.js';
import type { CaseMessageView } from './CasesReaderPort.js';
import { formatMessageDateTime } from './formatMessageDateTime.js';

/** The literal author string the integration may report for a system-posted message (IS #90). */
const SYSTEM_AUTHOR = 'integration-service';

const blockClasses = 'rounded-md border px-4 py-3';
const ownBlockClasses = 'border-primary/20 bg-primary/5';
const fundBlockClasses = 'border-border bg-card';
const headerRowClasses = 'mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5';
const timestampClasses = 'text-xs text-muted-foreground whitespace-nowrap';
const bodyClasses = 'text-sm text-foreground whitespace-pre-wrap break-words leading-relaxed';

export function MessageThread({
  messages,
  active,
}: {
  readonly messages: ReadonlyArray<CaseMessageView>;
  readonly active: LanguageCode;
}): JSX.Element {
  const t = useTranslate();
  if (messages.length === 0) {
    return <p className="text-base text-muted-foreground">{t('myCases.detail.noMessages')}</p>;
  }
  return (
    // A list, not a stack of divs (the prototype uses divs): this IS a list of
    // messages, and the count is useful to a screen-reader user.
    <ul className="space-y-3 border-t border-border pt-5">
      {messages.map((m) => {
        const authorTrimmed = m.author !== null ? m.author.trim() : '';
        const isSystemAuthor = authorTrimmed.toLowerCase() === SYSTEM_AUTHOR;
        const isOwn = !m.isReply;
        const authorLabel = isOwn
          ? t('myCases.detail.selfAuthor')
          : isSystemAuthor || authorTrimmed.length === 0
            ? t('myCases.detail.systemAuthor')
            : m.author;
        return (
          <li
            key={m.id}
            className={`${blockClasses} ${isOwn ? ownBlockClasses : fundBlockClasses}`}
          >
            <div className={headerRowClasses}>
              <span
                className={`text-sm font-semibold ${isOwn ? 'text-foreground' : 'text-conversation-sender'}`}
              >
                {authorLabel}
              </span>
              <time className={timestampClasses} dateTime={m.createdAt}>
                {formatMessageDateTime(m.createdAt, active)}
              </time>
            </div>
            <p className={bodyClasses}>{m.text}</p>
          </li>
        );
      })}
    </ul>
  );
}
